import React from "react";
import { Card, CardContent, Typography, Box, Grid } from "@mui/material";
import { yellow } from "@mui/material/colors";
import greenGem from "../@shared/assets/green_gem.webp";
import heart from "../@shared/assets/icons/heart.png";
import sheild from "../@shared/assets/icons/sheild.png";
import swords from "../@shared/assets/icons/swords_icon2.png";
import fire from "../@shared/assets/fire.png";
import ice from "../@shared/assets/ice.png";
import void1 from "../@shared/assets/void.png";
import wind from "../@shared/assets/wind.png";
import crossHair from "../@shared/assets/icons/cross-hair.png";
import water from "../@shared/assets/icons/water.png";
import grass from "../@shared/assets/icons/grass.png";
import rock from "../@shared/assets/icons/rock.png";
import electric from "../@shared/assets/icons/electric.png";
import { keyframes } from "@emotion/react"; // Enables CSS keyframe animations
import StylizedInfoSection from "./StylizedInfoSection";
import { SEASONS } from "../@shared/utils/Seasons";


const collectionsMap = [
  { name: 'Azuki', image: require('../@shared/assets/partners/azuki.png') },
  { name: 'Big Neighbors', image: require('../@shared/assets/partners/big.png') },
  { name: 'Bearish', image: require('../@shared/assets/partners/bearish.png') },
  { name: 'Buns (The Wilds)', image: require('../@shared/assets/partners/the-wilds.png') },
  { name: 'Crypto Meow Meows', image: require('../@shared/assets/partners/cmm.png') },
  { name: 'Dragon Punks', image: require('../@shared/assets/partners/dragon-punks.png') },
  { name: 'Froggy Friends', image: require('../@shared/assets/partners/froggy-friends.png') },
  { name: 'LAMBs', image: require('../@shared/assets/partners/lambs.png') },
  { name: 'Solana Deads', image: require('../@shared/assets/partners/solana-deads.png') },
  { name: 'Solana Strays', image: require('../@shared/assets/partners/solana-strays.png') },
  { name: 'Tadpoles', image: require('../@shared/assets/partners/tadpoles.png') },
  { name: 'The Alien Boy', image: require('../@shared/assets/partners/alien-boy.png') },
  { name: 'The Quakkpak', image: require('../@shared/assets/partners/quakk.png') },
  { name: 'Snorkel Explorers', image: require('../@shared/assets/partners/snorkel.jpg') },
  { name: 'Crypt Social Club', image: require('../@shared/assets/partners/csc.png') },
  { name: 'Wolfpack', image: require('../@shared/assets/partners/wolfpack.png') },
  { name: 'The Energy Wabbits', image: require('../@shared/assets/partners/ewabbits.jpg') },
  { name: 'Bae Apes (BaeBiez)', image: require('../@shared/assets/partners/baeapes.jpg') },
]

const clampByte = (value) => Math.max(0, Math.min(255, value));

const parseCssColorToRgb = (color) => {
  const value = String(color || "").trim();

  if (!value) return null;

  if (value.startsWith("#")) {
    const hex = value.slice(1);
    const normalized =
      hex.length === 3
        ? hex
            .split("")
            .map((char) => char + char)
            .join("")
        : hex.length === 6
          ? hex
          : "";

    if (!normalized) return null;

    return {
      r: Number.parseInt(normalized.slice(0, 2), 16),
      g: Number.parseInt(normalized.slice(2, 4), 16),
      b: Number.parseInt(normalized.slice(4, 6), 16),
    };
  }

  const rgbMatch = value.match(
    /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})/i,
  );

  if (!rgbMatch) return null;

  return {
    r: clampByte(Number(rgbMatch[1])),
    g: clampByte(Number(rgbMatch[2])),
    b: clampByte(Number(rgbMatch[3])),
  };
};

const getReadableTextColor = (backgroundColor) => {
  const rgb = parseCssColorToRgb(backgroundColor);
  if (!rgb) return "#fff";

  const luminance =
    (0.2126 * rgb.r + 0.7152 * rgb.g + 0.0722 * rgb.b) / 255;

  return luminance > 0.62 ? "#000" : "#fff";
};


const CharacterCard = ({
  name,
  attackName,
  amountOwned,
  showAmtOwned = false,
  description,
  imageUrl,
  imageBG,
  health,
  accuracy,
  attack,
  defense,
  rarity,
  element,
  bgColor1,
  bgColor2,
  containerHeight,
  heightPer,
  borderPx = 8,
  onClick,
  labelSize = 10,
  version,
  artistName,
  artistStripeId,
  artistBadge,
  collectionName,
  collectionStripeId,
  number,
  cardNumber,
  season,
  shine = true,
  physicalAssetId,
}) => {
  const screenHeight = window.innerHeight;
  const aspectRatio = 2.5 / 3.5;

  const cardHeight = containerHeight
    ? (containerHeight * heightPer) / 100
    : (screenHeight * heightPer) / 100;
  const cardWidth = cardHeight * aspectRatio;

  const attributes = [
    { name: "Rarity", value: rarity?.split("")[0], img: greenGem },
    { name: "Health", value: health, img: heart },
    { name: "Defense", value: defense, img: sheild },
    { name: "Attack", value: attack, img: swords },
    { name: "Accuracy", value: accuracy, img: crossHair },
  ];

  const elementIconMap = {
    fire,
    water,
    grass,
    rock,
    air: wind,
    electric,
    void: void1,
    ice,
  };
  const elementImg = elementIconMap[element] || null;
  const normalizedCollectionName = String(collectionName || "").trim();
  const collectionLogo = collectionsMap.find(
    (c) => c.name?.toLowerCase() === normalizedCollectionName.toLowerCase(),
  )?.image;
  const showCollectionMeta = normalizedCollectionName.length > 0;
  const normalizedSeason = Number.isFinite(Number(season))
    ? Number(season)
    : null;
  const resolvedSeason = normalizedSeason
    ? SEASONS.find((s) => Number(s.season) === normalizedSeason)
    : null;
  const seasonDisplay = normalizedSeason
    ? `S: ${normalizedSeason}${
        resolvedSeason?.name ? ` (${resolvedSeason.name})` : ""
      }`
    : "";
  const rawCardNumber =
    number !== undefined && number !== null && String(number).trim() !== ""
      ? String(number).trim()
      : cardNumber !== undefined &&
          cardNumber !== null &&
          String(cardNumber).trim() !== ""
        ? String(cardNumber).trim()
        : "";
  const physicalAssetLabel = physicalAssetId
    ? `AID ${String(physicalAssetId).trim().toUpperCase()}`
    : "";
  const cardMeta = [
    rawCardNumber ? `#${rawCardNumber}` : null,
    `V: ${version ? version : "001"}`,
    seasonDisplay || null,
    physicalAssetLabel || null,
  ]
    .filter(Boolean)
    .join(" | ");

  const bannerNameSize = "8.5cqw";
  const attackNameSize = "6cqw";
  const attrTextSize = "9cqw";
  let dynamicLabelSize = cardWidth * 0.04;
  const sectionLabelSize = labelSize * 1.2;
  const sectionContentSize = cardHeight * 0.65;
  const cardBorderRadius = cardHeight * 0.05;
  const cardBorderWidth = cardHeight * 0.015;
  const cardBackgroundPadding = cardHeight * 0.01;
  const cardBorderRadiusInner = "5px";
  const bottomContentPadding = cardHeight * 0.01;
  const infinityImageTopMargin = cardHeight * 0.13;
  const cotainerPadding = cardHeight * 0.03;
  const rarityTextSize = cardHeight * 0.05;
  const artistMargin = cardHeight * 0.02;
  const descFontSize = "5cqw";
  const detailsFontSize = "2cqw";
  const descIconSize = "8%";
  const maxTextSize = "50cqw";
  const detailsPanelBottomColor =
    rarity !== "Epic" && rarity !== "Legendary" ? "#ffffff" : bgColor1;
  const detailsTextColor = getReadableTextColor(detailsPanelBottomColor);

  const iconMap = { swords, sheild, heart, crossHair };
  const triggerWordToIconKey = {
    attack: "swords",
    defense: "sheild",
    health: "heart",
    accuracy: "crossHair",
  };

  const holoAnimation = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

  const glossAnimation = keyframes`
  0% {
    transform: translateX(-150%) skewX(-20deg);
  }
  100% {
    transform: translateX(150%) skewX(-20deg);
  }
`;

  const getElementBGColor = () => {
    switch (element) {
      case "fire":
        return "rgb(255, 205, 130)";
      case "water":
        return "rgb(183, 216, 255)";
      case "grass":
        return "rgb(185, 255, 188)";
      case "rock":
        return "rgb(255, 234, 169)";
      case "air":
        return "rgb(209, 227, 235)";
      case "electric":
        return "rgb(255, 252, 173)";
      case "void":
        return "rgb(188, 154, 201)";
      case "ice":
        return "rgb(227, 246, 255)";
      default:
        return "#c1c1c1";
    }
  };

  const attackBGColor = getElementBGColor();

  const parseTextWithIcons = (text) => {
    // split on words, whitespace, and punctuation
    const parts = text.split(/(\s+|[.,!?])/g);
    return parts.map((part, i) => {
      const key = part.toLowerCase().replace(/[^a-z]/g, "");
      const iconKey = triggerWordToIconKey[key];
      if (iconKey && iconMap[iconKey]) {
        return (
          <Box
            key={`icon-${i}`}
            component="img"
            src={iconMap[iconKey]}
            alt={iconKey}
            style={{
              width: descIconSize,
              height: "auto",
              objectFit: "contain",
              mx: "2%",
            }}
          />
        );
      }
      return (
        <Typography
          key={`text-${i}`}
          component="span"
          sx={{
            fontSize: descFontSize,
            display: "inline",
            // verticalAlign: "middle",
          }}
        >
          {part}
        </Typography>
      );
    });
  };

  return (
    <Box
      height={`${heightPer}%`}
      width="auto"
      sx={{ aspectRatio: "2.5 / 3.5", position: "relative" }} // Maintain trading-card aspect ratio
      onClick={onClick}
    >
      {showAmtOwned && (
        <Box
          style={{
            backgroundColor: "#00000090",
            padding: "2% 4%",
            borderRadius: 20,
            width: "12%",
            position: "absolute",
            top: "-5%",
            right: "-5%",
            zIndex: 10,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
            containerType: "inline-size",
          }}
        >
          <Typography
            variant="body2"
            style={{
              fontSize: maxTextSize,
              fontWeight: "bold",
              color: "#fff",
            }}
          >
            {amountOwned}
          </Typography>
        </Box>
      )}

      <Card
        elevation={0}
        sx={{
          height: "100%",
          width: "100%",
          display: "flex",
          boxShadow:'none',
          flexDirection: "column",
          borderRadius: "4%",
          overflow: "hidden",
          position: "relative",
          padding: "3%",
          // position: "absolute",
          zIndex: 0,
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          background: yellow[700], // Same base color as InfinityCard
          background: `linear-gradient(0deg, ${bgColor1} 0%, ${bgColor2} 100%)`,
          containerType: "inline-size",
          animation:
           (shine && ( rarity === "Epic" ||
            rarity === "Legendary")) && `${holoAnimation} 5s ease infinite`,
          // borderRadius: "1%",
        }}
      >
        {/* Absolute gradient layer */}
        {(shine && (rarity === "Epic" || rarity === "Legendary")) && (
          <>
            <Box
              sx={{
                position: "absolute",
                zIndex: 0,
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                height: "94%",
                width: "93%",
                backgroundImage:
                  rarity !== "Epic" && rarity !== "Legendary"
                    ? `linear-gradient(to bottom, ${bgColor1}, ${bgColor2})`
                    : `linear-gradient(to bottom, ${bgColor2}, ${bgColor1})`,
                borderRadius: "8px",
              }}
            />

            <Box
              sx={{
                position: "absolute",
                zIndex: 2, // Above the existing layers
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                pointerEvents: "none",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "200%", // Extend the width for smooth movement
                  height: "100%",
                  background:
                    "linear-gradient(120deg, transparent 20%, rgba(228, 228, 228, 0.36) 50%, transparent 80%)",
                  transform: "skewX(-20deg)",
                  animation: `${glossAnimation} 3s ease-in-out infinite`,
                },
              }}
            />
          </>
        )}

        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            // background:
            //   "linear-gradient(45deg, rgba(255,255,255,0.3), rgba(255,255,255,0) 70%)",
            mixBlendMode: "",
            pointerEvents: "none",
            borderRadius: "inherit",
          }}
        >
          {attributes.map((attribute, index) => {
            const { value, img } = attribute;
            return (
              <Box
                key={index}
                style={{
                  ...styles.attributeContainer,
                  left: `${index === 0 ? 0.5 : -1}%`,
                  top: `${index === 0 ? -5 : index * 10 - 4}%`,
                  zIndex: 10,
                  width:
                    index === 0
                      ? "11%"
                      : index === attributes.length - 1
                        ? "15%"
                        : "14%",

                  // { backgroundColor: "red" },
                }}
              >
                <Box
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                    height: "100%",
                    width: "100%",
                  }}
                >
                  <img src={img} alt="" style={styles.attributeImg} />
                  <Typography
                    style={{
                      ...styles.attributeText,
                      fontSize: attrTextSize,
                    }}
                  >
                    {value}
                  </Typography>
                </Box>
              </Box>
            );
          })}

          <Box
            sx={{
              height: "100%",
              width: "100%",
              display: "flex",
              flexDirection: "column",
              borderRadius: "3.5%",
              overflow: "visible",
              position: "relative",
              zIndex: 0,
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              overflow: "hidden",
            }}
          >
            {(rarity === "Epic" || rarity === "Legendary" || imageBG) && imageBG && (
              <img
                src={imageBG}
                style={{
                  height: "100%",
                  width: "auto",
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  zIndex: 0,
                }}
              />
            )}
            <CardContent
              style={{ paddingBottom: 0 }}
              sx={{ height: "100%", padding: 0 }}
            >
              {/* Top portion: name banner, gem, and character image */}
              <Grid
                container
                item
                xs={12}
                sx={{
                  position: "relative",
                  height: "55%",
                  padding: "2%",
                }}
              >
                {/* Character Image */}

                <img
                  src={imageUrl}
                  alt=""
                  style={{
                    height: version ? "130%" : "110%",
                    width: "auto",
                    position: "absolute",
                    top: "80%",
                    left: version ? '50%' :
                      rarity === "Epic" || rarity === "Legendary"
                        ? "38%"
                        : "50%",
                    transform: "translate(-50%, -50%)",
                    zIndex: 0,
                    filter: "none",
                  }}
                />

                {/* Skewed banner for name */}

                <Box
                  sx={{
                    height: "14%",
                    backgroundColor: "#ffffff95",
                    zIndex: 1,
                    width: "86%",
                    // transform: "skewX(-20deg)",
                    borderRadius: "0px 50px 50px 0px",
                    position: "absolute",
                    top: "2%",
                    left: "5%",
                    paddingLeft: "5%",
                    display: "flex",
                    alignItems: "center",
                    containerType: "inline-size",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: bannerNameSize,
                      fontWeight: "bold",
                      marginLeft: "3%",
                    }}
                  >
                    {name || "Character Name"}
                  </Typography>
                </Box>

                {showCollectionMeta && (<>
                  <Box
                    sx={{
                      height: "7%",
                      // backgroundColor: "#ffffff95",
                      // boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.2)",
                      zIndex: 2,
                      width: "56%",
                      // transform: "skewX(-20deg)",
                      textShadow: ` -1px -1px 0 #fff, 1px -1px 0 #fff, -1px  1px 0 #fff, 1px  1px 0 #fff`,
                      borderRadius: "50px 0px 0px 50px",
                      position: "absolute",
                      top: "12%",
                      right: "5%",
                      paddingRight: "5%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: 'right',
                      containerType: "inline-size",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: attackNameSize,
                        fontWeight: "bold",
                        marginRight: "10%",
                      }}
                    >
                      {normalizedCollectionName}
                    </Typography>
                  </Box>

                  {collectionLogo && (
                    <img
                      style={{
                        height: "10%",
                        zIndex: 10,
                        width: "auto",
                        position: "absolute",
                        top: "10%",
                        right: "5%",
                        borderRadius: '15%',
                        border: '2px solid white'
                      }}
                      src={collectionLogo}
                    />
                  )}
                </>)
                }


              </Grid>

              {/* Bottom portion: details/stats box */}
              <Grid
                container
                item
                xs={12}
                sx={{
                  height: "40%", //! Adjust as needed
                  position: "relative",
                  justifyContent: "center",
                  alignItems: "flex-end",
                }}
              >
                
                {/* Versioning Box */}
                {artistBadge && (
                  <img
                    style={{
                      height: "15%",
                      zIndex: 10,
                      width: "auto",
                      position: "absolute",
                      bottom: "-12%",
                      right: "1%",
                      borderRadius: '15%',
                    }}
                    src={artistBadge}
                    alt=''
                  />
                )}
                <Box sx={{ zIndex: 10, position: 'absolute', width: '80%', display: 'flex', flexDirection: 'row', justifyContent: 'space-between', px: '2%', bottom: '-13%', left: '45%', transform: 'translateX(-50%)', }}>
                  <Typography style={{
                    fontSize: detailsFontSize,
                    fontWeight: 400,
                    color: detailsTextColor,
                    
                  }}>
                    {cardMeta}
                  </Typography>
                  {artistName && <Typography style={{
                    fontSize: detailsFontSize,
                    fontWeight: 400,
                    color: detailsTextColor,
                    marginRight: -artistMargin,
                  }}>Art by: {artistName}</Typography>}
                </Box>
                <Box
                  sx={{
                    height: "100%",
                    width: "104.5%",
                    position: "absolute",
                    top: "20%",
                    left: "50%",
                    transform: "translateX(-50%)",
                    //   padding: "1%",
                    //   borderRadius: cardBorderRadiusInner,
                  }}
                >
                  <Box
                    style={{
                      position: "relative",
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <Box
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                      }}
                    >
                      <StylizedInfoSection
                        width={`100%`}
                        height={"100%"}
                        topColor={bgColor1}
                        bottomColor={detailsPanelBottomColor}
                        id={name?.split(" ")?.join("_") + bgColor1}
                      />
                    </Box>

                    <Box
                      sx={{
                        width: "60%", // makes it narrower than the white box
                        mx: "auto", // center horizontally
                        backgroundColor: attackBGColor,
                        borderRadius: "100px",
                        border: `${(cardBorderWidth * 0.25).toFixed(3)}px solid white`,
                        display: "inline-flex", // shrink‐to‐fit
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "2%",
                        position: "absolute",
                        right: "2%",
                        top: "0%",
                        zIndex: 500,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: attackNameSize,
                          fontWeight: 900,
                          color: "#000",
                        }}
                      >
                        {attackName}
                      </Typography>
                      {elementImg && (
                        <img
                          src={elementImg}
                          alt={""}
                          style={{
                            width: `8%`,
                            height: "auto",
                            objectFit: "contain",
                          }}
                        />
                      )}
                    </Box>

                    <Box
                      sx={{
                        width: "80%", // same width as attack nametag
                        height: "45%",
                        backgroundColor: "#ffffff50",
                        borderRadius: cardBorderRadiusInner,
                        mx: "auto", // center horizontally
                        mt: `${dynamicLabelSize * 2.5}px`, // small top margin
                        display: "inline-flex", // shrink‐to‐fit
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "2% 5%",
                        gap: 1, // if you ever add an icon
                        position: "relative", // for absolute positioning of the icon
                      }}
                    >
                      <Typography
                        component="div"
                        sx={{
                          mb: 2,
                          display: "inline-flex",
                          flexWrap: "wrap",
                          alignItems: "center",
                          gap: 0.25,
                          mt: 2,
                        }}
                      >
                        {parseTextWithIcons(description || "")}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>
            </CardContent>
          </Box>
        </Box>
      </Card>
    </Box>
  );
};

const styles = {
  cardContainer: {
    aspectRatio: 2.5 / 3.5,
    borderRadius: 16,
    // overflow: "hidden",
    position: "relative",
    margin: 8,
  },
  cardShadow: {
    flex: 1,
    borderRadius: 16,
    borderColor: "#FFD700",
    // overflow: "hidden",
  },
  cardBackground: {
    flex: 1,
    padding: 10,
    borderRadius: 10,
    justifyContent: "space-between",
  },
  topContent: {
    flex: 3,
    alignItems: "center",
    justifyContent: "flex-start",
    position: "relative",
  },
  gemImage: {
    width: "12%",
    height: "20%",
    position: "absolute",
    top: "-4%",
    left: "-3%",
    zIndex: 2,
  },
  attributeText: {
    fontWeight: "900",
    color: "#ffff",
    textShadow: ` -1px -1px 0 #000, 1px -1px 0 #000, -1px  1px 0 #000, 1px  1px 0 #000`,
    position: "absolute",
  },
  attributeImg: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
  },
  attributeContainer: {
    width: "15%",
    height: "20%",
    position: "absolute",
    left: "-1%",
    zIndex: 100,
  },
  titleBanner: {
    backgroundColor: "#ffffffcc",
    width: "90%",
    position: "absolute",
    padding: 4,
    paddingLeft: "6%",
    top: "0%",
    left: "5%",
    zIndex: 1,
    transform: [{ skewX: "-20deg" }],
    overflow: "hidden",
  },
  name: {
    fontWeight: "900",
    fontSize: 20,
    textAlign: "left",
  },
  infinityImage: {
    width: "85%",
    height: "70%",
    marginTop: "15%",
    zIndex: 0,
  },
  bottomContent: {
    flex: 2,
    padding: 8,
    position: "relative",
  },
  descriptionBox: {
    backgroundColor: "#fff",
    padding: 10,
    borderRadius: 10,
    height: "95%",
    elevation: 5,
    shadowColor: "#000",
    shadow: 2,
    shadowOpacity: 0.3,
    shadowRadius: 4,
  },
  sectionLabel: {
    fontWeight: "900",
    fontSize: 16,
    marginTop: 4,
  },
  sectionValue: {
    fontSize: 14,
  },
  rarity: {
    position: "absolute",
    bottom: 0,
    right: 0,
    fontWeight: "900",
    fontSize: 18,
    color: "#000",
  },
};

export default CharacterCard;
